import './style.css';
import {
  MatchPhase,
  ClientMsg,
  isValidCode,
  codeToSeed,
  seedToCode,
  randomSeed,
  type Difficulty,
} from '@static-pines/shared';

import { AudioEngine } from './audio/AudioEngine.js';
import { Connection } from './net/Connection.js';
import { Game } from './game/Game.js';
import { IS_TOUCH_DEVICE } from './game/Input.js';
import { Hud, TransmitPanel } from './ui/Hud.js';
import { Menu } from './ui/Menu.js';
import { MIC_SVG, MIC_SVG_MUTED } from './ui/icons.js';

const app = document.getElementById('app')!;

const audio = new AudioEngine();
const connection = new Connection();

const hud = new Hud(app);
const transmit = new TransmitPanel(app);
hud.setVisible(false);

let game: Game | null = null;
let inLobby = false;

/**
 * Guards against joining twice from one browser.
 *
 * Without this, loading a URL that carries a room code auto-joins, and then
 * clicking a menu button joins *again* — two sessions from one tab, the second
 * of which is not the host and so cannot start the match. Easy to mistake for
 * a server bug.
 */
let connecting = false;

// --- Pointer-lock prompt -----------------------------------------------------

const lockPrompt = document.createElement('div');
lockPrompt.id = 'lock-prompt';
lockPrompt.classList.add('hidden');

/**
 * When the pause overlay was last opened.
 *
 * The tap that presses the pause button is the same tap the overlay then
 * receives: pointerdown fires the pause event, the overlay appears, and the
 * tap's click lands on the overlay — which reads as "resume". The overlay
 * flashed for a frame and the game unpaused itself. Ignoring clicks in the
 * overlay's first moments makes opening and closing two separate gestures.
 */
let pauseShownAt = 0;

lockPrompt.addEventListener('click', (e) => {
  // The leave button is inside the overlay but is not a "resume" click.
  if ((e.target as HTMLElement).closest('#leave-game')) {
    void returnToTitle();
    return;
  }

  // Player rows toggle mics and keep the overlay open.
  const row = (e.target as HTMLElement).closest('.pause-player-row');
  if (row) {
    const sessionId = row.getAttribute('data-session-id');
    if (sessionId) {
      togglePlayerMute(sessionId);
      renderLockPrompt();
    }
    return;
  }

  if (performance.now() - pauseShownAt < 350) return;

  void audio.start();

  if (IS_TOUCH_DEVICE) {
    /**
     * No pointer lock exists on touch devices, so the overlay is dismissed by
     * hand and "paused" is simply this overlay being shown again. Fullscreen
     * and landscape are best-effort: Android honours them, iOS Safari has no
     * fullscreen API for pages and simply ignores the request.
     */
    hasLockedOnce = true;
    renderLockPrompt();
    lockPrompt.classList.add('hidden');
    void document.documentElement
      .requestFullscreen?.()
      .then(() => (screen.orientation as unknown as { lock?: (o: string) => Promise<void> }).lock?.('landscape'))
      .catch(() => undefined);
    return;
  }

  game?.input.requestLock();
});
app.appendChild(lockPrompt);

// The touch pause button raises this; tapping the overlay resumes.
window.addEventListener('static-pines:pause', () => {
  if (game && !inLobby) {
    pauseShownAt = performance.now();
    lockPrompt.classList.remove('hidden');
  }
});

// Gentle, purely-CSS-gated hint: only renders on coarse pointers in portrait.
const rotateHint = document.createElement('div');
rotateHint.id = 'rotate-hint';
rotateHint.textContent = 'ROTATE FOR A WIDER VIEW';
app.appendChild(rotateHint);

/**
 * The overlay doubles as the pause screen: releasing the pointer (Esc) brings
 * it back. Wording changes after the first lock so a mid-game reappearance
 * reads as "you paused", not "the game failed to start".
 */
let hasLockedOnce = false;

function renderLockPrompt(): void {
  // The paused state is a small menu: resume by tapping anywhere, mute mics
  // by clicking players, or leave.
  const leave = '<button id="leave-game">LEAVE GAME</button>';
  const roster = pauseRosterHtml();
  if (IS_TOUCH_DEVICE) {
    lockPrompt.innerHTML = hasLockedOnce
      ? `<h2>PAUSED</h2><p>TAP TO RESUME</p>${roster}${leave}`
      : '<h2>TAP TO PLAY</h2><p>LEFT STICK MOVE · DRAG TO LOOK · TAP SLOT TO USE ITEM · HOLD TO DROP</p>';
    return;
  }
  lockPrompt.innerHTML = hasLockedOnce
    ? `<h2>PAUSED</h2><p>CLICK TO RESUME</p>${roster}${leave}`
    : '<h2>CLICK TO PLAY</h2><p>WASD MOVE · SHIFT RUN · CTRL CROUCH · F LIGHT · R RADIO · V MIC · E INTERACT</p>';
}

/**
 * The pause menu's player roster — the in-game home of the same mic controls
 * the lobby has, because pausing is the one moment both platforms have a free
 * pointer. Built fresh each time the overlay renders, so it never goes stale.
 */
function pauseRosterHtml(): string {
  const state = connection.state;
  if (!game || !state || state.players.size === 0) return '';

  const rows = [...state.players.entries()]
    .map(([sessionId, player]) => {
      const you = sessionId === connection.sessionId;
      const muted = you ? game!.voice.muted : game!.voice.isPeerMuted(sessionId);
      const label = `${you ? '▸ ' : ''}${escapeHtml(player.name)}`;
      const title = you ? 'Mute / unmute your microphone' : `Mute ${escapeHtml(player.name)} for you`;
      return (
        `<div class="pause-player-row" data-session-id="${sessionId}" title="${title}">` +
        `<span>${label}</span>` +
        `<span class="mic-indicator${muted ? ' muted' : ''}">${muted ? MIC_SVG_MUTED : MIC_SVG}</span>` +
        `</div>`
      );
    })
    .join('');

  return `<div class="pause-roster">${rows}</div>`;
}

/** Player names are user text going into innerHTML. Never trust them raw. */
function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

/** One rule for every surface: your row is your mic, theirs is your ears. */
function togglePlayerMute(sessionId: string): void {
  if (!game) return;
  if (sessionId === connection.sessionId) game.voice.toggleMute();
  else game.voice.togglePeerMute(sessionId);
}
renderLockPrompt();

document.addEventListener('pointerlockchange', () => {
  const locked = !!document.pointerLockElement;
  if (locked) hasLockedOnce = true;
  renderLockPrompt();
  if (game && !inLobby) lockPrompt.classList.toggle('hidden', locked);
});

// --- Debug overlay -----------------------------------------------------------

const debug = document.createElement('div');
debug.id = 'debug';
debug.classList.add('hidden');
app.appendChild(debug);

window.setInterval(() => {
  if (game && !debug.classList.contains('hidden')) debug.textContent = game.debugText;
}, 250);

window.addEventListener('keydown', (e) => {
  if (e.code === 'F3') debug.classList.toggle('hidden');
  if (e.code === 'KeyM' && document.pointerLockElement) audio.setMuted(!audio.isMuted);
});

// --- Menu --------------------------------------------------------------------

const menu = new Menu(app, {
  // Hosting and solo both mint a fresh code. Passing no code would let the
  // server's `filterBy(['code'])` matchmaking drop unrelated players into the
  // same codeless room — the opposite of a private lobby.
  onSolo: (name, difficulty) => void connect(name, seedToCode(randomSeed()), true, difficulty),
  onHost: (name, difficulty) => void connect(name, seedToCode(randomSeed()), false, difficulty),
  // Joiners adopt the room's difficulty — it shapes the world, so it cannot
  // differ between players in the same match.
  onJoin: (name, code) => void connect(name, code, false),
  onStartMatch: () => connection.send(ClientMsg.StartMatch),
  onReturnToTitle: () => void returnToTitle(),
  // The server trims and applies; state sync repaints every lobby.
  onRename: (name) => connection.send(ClientMsg.SetName, name),
  onTogglePlayerMute: (sessionId) => {
    togglePlayerMute(sessionId);
    refreshLobby();
  },
});

menu.showTitle();

// A code in the URL joins that room directly: #ABC234
const hash = location.hash.replace('#', '').toUpperCase();
if (isValidCode(hash)) {
  const savedName = localStorage.getItem('static-pines.name') ?? 'Survivor';
  void connect(savedName, hash, false);
}

// --- Connection lifecycle ----------------------------------------------------

async function connect(
  name: string,
  code: string | undefined,
  solo: boolean,
  difficulty?: number,
): Promise<void> {
  if (connecting || connection.room) return;
  connecting = true;

  try {
    await audio.start();

    connection.setHandlers({
      onWelcome: (msg) => {
        location.hash = msg.code;

        game = new Game(app, audio, connection, hud, transmit);
        game.buildWorld(msg.seed, msg.difficulty);
        game.syncSpawn();
        game.start();

        // Keep the canvas behind every overlay.
        const canvas = game.renderer.domElement;
        canvas.style.position = 'absolute';
        canvas.style.inset = '0';
        canvas.style.zIndex = '0';

        enterLobby();

        /**
         * Voice starts here rather than at match start so the lobby has it
         * too — and so the permission prompt appears while players are safe,
         * not mid-forest. The click that connected is a fresh user gesture,
         * which mic permission requests require.
         */
        void game.voice.start(msg.ice ?? []);

        // Solo players skip the lobby entirely.
        if (solo) connection.send(ClientMsg.StartMatch);
      },

      onVoiceSignal: (msg) => void game?.voice.onSignal(msg),

      onCue: (msg) => game?.onCue(msg.kind, msg.x, msg.y, msg.z, msg.gain),
      onCaught: (msg) => game?.onCaught(msg.sessionId),
      onConfront: (msg) => game?.onConfront(msg.x, msg.y, msg.z, msg.duration),
      onClue: (msg) => game?.onClue(msg.text),
      onNotice: (msg) => game?.onNotice(msg.text, msg.duration),
      onCorrection: (msg) => game?.onCorrection(msg.x, msg.y, msg.z),

      onMatchEnded: (msg) => {
        hud.setVisible(false);
        lockPrompt.classList.add('hidden');
        document.exitPointerLock?.();
        menu.showEnd(msg.won, msg.reason, msg.survivors);
      },

      onPlayerAdd: () => refreshLobby(),
      onPlayerRemove: () => refreshLobby(),

      onLeave: () => {
        if (game) menu.showTitle();
      },
      onError: (message) => menu.showError(message),
    });

    await connection.join(name, code, difficulty);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    menu.showError(`Could not connect: ${message}`);
    menu.showTitle();
  } finally {
    connecting = false;
  }
}

function enterLobby(): void {
  inLobby = true;
  refreshLobby();

  // Watch for the match starting. Colyseus state changes are polled here
  // rather than subscribed, because a single check per frame is simpler than
  // wiring a listener for one boolean.
  const poll = window.setInterval(() => {
    const state = connection.state;
    if (!state) return;

    if (state.phase !== MatchPhase.Lobby && inLobby) {
      inLobby = false;
      window.clearInterval(poll);
      menu.hide();
      hud.setVisible(true);
      lockPrompt.classList.remove('hidden');
      game?.syncSpawn();
    } else if (inLobby) {
      refreshLobby();
    }
  }, 250);
}

function refreshLobby(): void {
  const state = connection.state;
  if (!state || !inLobby) return;

  const players = [...state.players.entries()].map(([sessionId, player]) => {
    const isYou = sessionId === connection.sessionId;
    return {
      sessionId,
      name: player.name,
      isHost: sessionId === state.hostSessionId,
      isYou,
      connected: player.connected,
      // Your own row lights from your mic's VAD; everyone else's from the
      // audio actually arriving over their peer connection.
      speaking: isYou
        ? (game?.voice.speaking ?? false)
        : (game?.voice.isSpeaking(sessionId) ?? false),
      muted: isYou
        ? (game?.voice.muted ?? false)
        : (game?.voice.isPeerMuted(sessionId) ?? false),
    };
  });

  menu.showLobby(
    state.code,
    players,
    connection.sessionId === state.hostSessionId,
    state.difficulty as Difficulty,
    state.towersRequired,
  );
}

async function returnToTitle(): Promise<void> {
  inLobby = false;
  hud.setVisible(false);
  transmit.hide();
  lockPrompt.classList.add('hidden');

  await connection.leave().catch(() => undefined);
  connecting = false;

  game?.dispose();
  game = null;

  // The next match starts fresh: its first overlay must read "play", not
  // "paused", and must not offer to leave a game that has not begun.
  hasLockedOnce = false;
  renderLockPrompt();

  // Strip the room code without leaving a bare "#" behind, and without the
  // navigation that assigning to location.hash would cause.
  history.replaceState(null, '', location.pathname + location.search);

  menu.showTitle();
}

// Expose for debugging.
Object.assign(window, { connection, audio, getGame: () => game, codeToSeed });
